import React, { useState, useEffect, useContext } from "react";
import {
  StyleSheet,
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  RefreshControl,
  ActivityIndicator,
  Alert,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { ClientContext } from "../context/ClientContext";
import { getDailySummary } from "../api/api";
import { formatCurrency, formatNumber, formatDate } from "../utils/formatters";
import StatCard from "../components/StatCard";
import SalesChart from "../components/SalesChart";
import SmsNotificationCard from "../components/SmsNotificationCard";

const DashboardScreen = ({ navigation }) => {
  const { selectedClient, isLoading: isClientLoading } =
    useContext(ClientContext);
  const [dailySummary, setDailySummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const loadDashboardData = async () => {
    if (!selectedClient) return;

    setIsLoading(true);
    setError(null);

    try {
      console.log(
        "🔍 Loading dashboard data for client:",
        selectedClient.clientId
      );

      const data = await getDailySummary(selectedClient.clientId);

      //console.log("📊 Raw API response:", JSON.stringify(data, null, 2));
      /*console.log("📈 Dashboard values:", {
        todaySales: data?.todaySales,
        totalInvoices: data?.totalInvoices,
        avgInvoiceValue: data?.avgInvoiceValue,
        itemsSold: data?.itemsSold,
        salesTrend: data?.salesTrend,
      }); */
      setDailySummary(data);
    } catch (err) {
      console.error("❌ Error loading dashboard data:", err);
      setError("Failed to load dashboard data. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDashboardData();
    setRefreshing(false);
  };

  useEffect(() => {
    if (selectedClient) {
      loadDashboardData();
    }
  }, [selectedClient]);

  const handleClientSelect = () => {
    navigation.navigate("ClientSelection");
  };

  const getSyncStatus = (lastSynced) => {
  if (!lastSynced) {
    return {
      color: "#e74c3c",
      text: "Never synced",
    };
  }

  const syncTime = new Date(lastSynced);
  const now = new Date();
  const diffMinutes = Math.floor((now - syncTime) / (1000 * 60));

  if (diffMinutes < 15) {
    return {
      color: "#27ae60",
      text: diffMinutes <= 1 ? "Synced just now" : `Synced ${diffMinutes} min ago`,
    };
  }

  if (diffMinutes < 60) {
    return {
      color: "#f39c12",
      text: `Synced ${diffMinutes} min ago`,
    };
  }

  const diffHours = Math.floor(diffMinutes / 60);

  if (diffHours < 4) {
    return {
      color: "#e67e22",
      text: `Synced ${diffHours} ${diffHours === 1 ? "hour" : "hours"} ago`,
    };
  }

  return {
    color: "#e74c3c",
    text: `Sync delayed - ${diffHours} hours ago`,
  };
};


const formatIST = (dateValue) => {
  if (!dateValue) return "Never";

  return new Date(dateValue).toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

  // Prepare chart data if available
  const getChartData = () => {
    if (!dailySummary?.salesTrend || dailySummary.salesTrend.length === 0) {
      return { data: [], labels: [] };
    }

    // Sort by date
    const sortedData = [...dailySummary.salesTrend].sort(
      (a, b) => new Date(a.date) - new Date(b.date)
    );

    // Extract last 7 days if more data is available
    const chartData = sortedData.slice(-7);

    return {
      data: chartData.map((item) => item.amount),
      labels: chartData.map((item) => {
        const date = new Date(item.date);
        return `${date.getDate()}/${date.getMonth() + 1}`;
      }),
    };
  };

  const { data: chartData, labels: chartLabels } = getChartData();

  if (isClientLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#3498db" />
        <Text style={styles.loadingText}>Loading client data...</Text>
      </View>
    );
  }

  if (!selectedClient) {
    return (
      <View style={styles.noClientContainer}>
        <Ionicons name="business-outline" size={64} color="#bdc3c7" />
        <Text style={styles.noClientText}>No Client Selected</Text>
        <Text style={styles.noClientSubtext}>
          Please select a client to view their sales dashboard.
        </Text>
        <TouchableOpacity
          style={styles.selectClientButton}
          onPress={handleClientSelect}
        >
          <Text style={styles.selectClientButtonText}>Select Client</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />
        }
      >
        <View style={styles.header}>
          <View>  
            <TouchableOpacity
              style={styles.clientSelector}
              onPress={handleClientSelect}
            >
              <Text style={styles.clientName}>{selectedClient.clientName}</Text>
              <Ionicons name="chevron-down" size={16} color="#3498db" />
            </TouchableOpacity>
          
            <Text style={styles.syncTime}>
            {dailySummary?.lastSynced
              ? `Last sync: ${formatIST(dailySummary.lastSynced)}`
              : "No sync information"}{"  "}
              <TouchableOpacity onPress={handleRefresh}>
            <Ionicons
              name="refresh-outline"
              size={20}
              color="#7f8c8d"
            />
          </TouchableOpacity>
          </Text>
           </View>
            
          
        </View>

        {error ? (
          <View style={styles.errorContainer}>
            <Ionicons name="alert-circle-outline" size={48} color="#e74c3c" />
            <Text style={styles.errorText}>{error}</Text>
            <TouchableOpacity
              style={styles.retryButton}
              onPress={loadDashboardData}
            >
              <Text style={styles.retryButtonText}>Retry</Text>
            </TouchableOpacity>
          </View>
        ) : isLoading ? (
          <View style={styles.loadingSection}>
            <ActivityIndicator size="large" color="#3498db" />
            <Text style={styles.loadingText}>Loading dashboard data...</Text>
          </View>
        ) : (
          <>
            <View style={styles.statsRow}>
              <StatCard
                title="Today's Sales"
                value={formatCurrency(dailySummary?.todaySales || 0)}
                subtitle="Total sales amount for today"
                iconName="cash-outline"
                iconColor="#2ecc71"
                style={styles.statCard}
              />
              <StatCard
                title="Total Invoices"
                value={formatNumber(dailySummary?.totalInvoices || 0)}
                subtitle="Number of invoices today"
                iconName="receipt-outline"
                iconColor="#9b59b6"
                style={styles.statCard}
              />
            </View>

            <View style={styles.statsRow}>
              <StatCard
                title="Avg. Invoice Value"
                value={formatCurrency(dailySummary?.avgInvoiceValue || 0)}
                subtitle="Average invoice amount"
                iconName="calculator-outline"
                iconColor="#e67e22"
                style={styles.statCard}
              />
              <StatCard
                title="Items Sold"
                value={formatNumber(dailySummary?.itemsSold || 0)}
                subtitle="Total items sold today"
                iconName="basket-outline"
                iconColor="#3498db"
                style={styles.statCard}
              />
            </View>

            <SalesChart
              title="Sales Trend (Last 7 Days)"
              data={chartData}
              labels={chartLabels}
              type="line"
            />

            {/* Custom legend for payment mode pie chart */}
            {dailySummary?.paymentModes &&
              dailySummary.paymentModes.length > 0 && (
                <View style={{ marginVertical: 16, alignItems: "center" }}>
                  <SalesChart
                    type="pie"
                    data={dailySummary.paymentModes.map(
                      (pm) => parseInt(pm.totalamount) || 0
                    )}
                    labels={dailySummary.paymentModes.map(
                      (pm) => pm.paymentmode
                    )}
                    title="Payment Mode Distribution"
                    chartConfig={{
                      color: (opacity = 1) => `rgba(52, 152, 219, ${opacity})`,
                    }}
                   
                  />
                  {/* Custom legend below the chart */}
                  <View style={{ marginTop: 12 }}>
                    {dailySummary.paymentModes.map((pm, idx) => (
                      <View
                        key={pm.paymentmode}
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                          marginBottom: 4,
                          flexWrap: "wrap",
                        }}
                      >
                        <View
                          style={{
                            width: 12,
                            height: 12,
                            borderRadius: 6,
                            marginRight: 6,
                            backgroundColor: [
                              "#3498db",
                              "#e67e22",
                              "#2ecc71",
                              "#9b59b6",
                              "#e74c3c",
                              "#f1c40f",
                              "#1abc9c",
                            ][idx % 7],
                          }}
                        />
                        <Text
                          style={{
                            fontSize: 15,
                            color: "#2c3e50",
                            marginRight: 4,
                          }}
                        >
                          {pm.paymentmode}
                        </Text>
                        <Text style={{ fontSize: 14, color: "#7f8c8d" }}>
                          {formatCurrency(pm.totalamount)}
                        </Text>
                      </View>
                    ))}
                  </View>
                </View>
              )}

            <View style={styles.footer}>
  <View style={styles.syncInfo}>
    {(() => {
      const syncStatus = getSyncStatus(dailySummary?.lastSynced);

      return (
        <>
          <View style={styles.syncRow}>
            <View
              style={[
                styles.syncDot,
                { backgroundColor: syncStatus.color },
              ]}
            />

            <Text style={styles.footerText}>
              {syncStatus.text}
            </Text>
          </View>

          <Text style={styles.syncTime}>
            {dailySummary?.lastSynced
              ? `Last sync: ${formatIST(dailySummary.lastSynced)}`
              : "No sync information"}
          </Text>
        </>
      );
    })()}
  </View>

  <TouchableOpacity onPress={handleRefresh}>
    <Ionicons
      name="refresh-outline"
      size={20}
      color="#7f8c8d"
    />
  </TouchableOpacity>
</View>
          </>
        )}
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#f5f5f5",
  },
  scrollContent: {
    padding: 16,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
    marginBottom: 24,
  },
  greeting: {
    fontSize: 14,
    color: "#7f8c8d",
    marginBottom: 4,
  },
  clientSelector: {
    flexDirection: "row",
    alignItems: "center",
  },
  clientName: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#2c3e50",
    marginRight: 4,
  },
  dateText: {
    flexDirection: "row",
    alignItems: "center",
  },
  date: {
    fontSize: 14,
    color: "#7f8c8d",
    marginRight: 4,
  },
  statsRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 16,
  },
  statCard: {
    flex: 1,
    marginHorizontal: 4,
  },
  smsCard: {
    marginTop: 16,
    marginBottom: 16,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 16,
    marginBottom: 8,
  },
  footerText: {
    fontSize: 12,
    color: "#7f8c8d",
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#f5f5f5",
  },
  loadingSection: {
    justifyContent: "center",
    alignItems: "center",
    padding: 40,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    color: "#7f8c8d",
  },
  errorContainer: {
    alignItems: "center",
    justifyContent: "center",
    padding: 40,
  },
  errorText: {
    fontSize: 16,
    color: "#7f8c8d",
    textAlign: "center",
    marginTop: 16,
    marginBottom: 24,
  },
  retryButton: {
    backgroundColor: "#3498db",
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 5,
  },
  retryButtonText: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "500",
  },
  noClientContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#f5f5f5",
    padding: 20,
  },
  noClientText: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#2c3e50",
    marginTop: 16,
  },
  noClientSubtext: {
    fontSize: 16,
    color: "#7f8c8d",
    textAlign: "center",
    marginTop: 8,
    marginBottom: 24,
  },
  selectClientButton: {
    backgroundColor: "#3498db",
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
  },
  selectClientButtonText: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "500",
  },
});

export default DashboardScreen;
